import vanilla from 'OK/utils/vanilla';
// Подставляется при сборке, смотри `build-plugins/ok-icons.ts`
import getFullIconUrl from 'icons';

interface IconData {
    readonly viewBox: string;
    readonly content: string;
}

type ILazyIconListener = (lazyIconsCache: LazyIconsCache) => void;

export default class LazyIconsCache {
    /**
     * Регулярное выражение для вырезания контента из svg без `<svg></svg>`
     */
    private static readonly REGEXP_SVG_CONTENT = /^\s*<svg[^>]*>([\s\S]*)<\/svg>\s*$/;
    private static readonly FALLBACK_ICONS_BASE_URL = '/res/s/';
    private static readonly CACHE: Record<string, LazyIconsCache> = {};

    public readonly name: string;
    public iconName: string | null = null;
    public iconSize: number | null = null;
    public iconData: IconData | null = null;
    public html: string = '';
    public node: HTMLElement | null = null;
    public loadingPromise: Promise<void | LazyIconsCache> | null = null;
    public listeners: ILazyIconListener[] = [];

    public constructor(name: string) {
        this.name = name;
    }

    public getIconName(): string {
        if (this.iconName === null) {
            const name = this.name;
            const pos = name.lastIndexOf('/');
            if (pos !== -1) {
                this.iconName = name.substring(pos + 1);
            } else {
                this.iconName = name;
            }
        }
        return this.iconName;
    }

    public getIconSize(): number {
        if (this.iconSize === null) {
            const name = this.name;
            const pos = name.lastIndexOf('_');
            if (pos !== -1) {
                const size = name.substring(pos + 1);
                this.iconSize = isNaN(Number(size)) ? 16 : parseInt(size, 10);
            } else {
                this.iconSize = 16;
            }
        }

        return this.iconSize;
    }

    public getIconData(): IconData | null {
        if (this.node && !this.iconData) {
            this.iconData = {
                viewBox: this.node.getAttribute('viewBox')!,
                content: LazyIconsCache.REGEXP_SVG_CONTENT.exec(this.html)?.[1]!
            };
        }

        return this.iconData;
    }

    public getNode(className: string): HTMLElement | undefined {
        if (this.node) {
            return this.clone(className);
        }
    }

    public get(className: string): HTMLElement | undefined {
        if (this.node) {
            return this.clone(className);
        }

        void this.load();
    }

    public clone(className?: string): HTMLElement {
        const newNode = this.node?.cloneNode(true) as HTMLElement;

        if (className) {
            newNode.setAttribute('class', className);
        }

        return newNode;
    }

    public load = (): Promise<void | LazyIconsCache> => {
        if (!this.loadingPromise) {
            this.loadingPromise = vanilla.ajax<string>({
                url: LazyIconsCache.buildUrl(this.name),
                type: 'GET'
            }).then(res => {
                if (res && res.response) {
                    this.makeNode(res.response);
                    this.notify();
                } else {
                    this.loadingPromise = null;
                }
                return this;
            })
        }

        return this.loadingPromise;
    }

    public addListener(listener: ILazyIconListener): void {
        this.listeners.push(listener);
    }

    public removeListener(listener: ILazyIconListener): void {
        const index = this.listeners.indexOf(listener);

        if (index !== -1) {
            this.listeners.splice(index, 1);
        }
    }

    public notify = () => this.listeners.forEach(listener => void listener(this));

    public makeNode(html: string): HTMLElement {
        const div = document.createElement('div');
        div.innerHTML = html;
        this.html = html;
        this.node = div.removeChild(div.firstChild!) as HTMLElement;

        return this.node;
    }

    public static getInstance(name: string): LazyIconsCache {
        if (!name) {
            throw new Error('Не задано имя иконки');
        }

        if (!LazyIconsCache.CACHE[name]) {
            LazyIconsCache.CACHE[name] = new LazyIconsCache(name);
        }

        return LazyIconsCache.CACHE[name];
    };

    private static buildUrl(name: string): string {
        const fullUrl = getFullIconUrl(name);

        if (fullUrl) {
            return fullUrl;
        }

        window.OK.Tracer.error(new Error(`Иконка ${name} отсутствует в карте с хешами`));
        // если иконка не нашлась, делаем фоллбек на прямой путь до иконки без хеша
        return `${LazyIconsCache.FALLBACK_ICONS_BASE_URL}${name}.svg`;
    }
}

export function getInstance(name: string): LazyIconsCache {
    return LazyIconsCache.getInstance(name);
}
